import { Result } from 'core/infrastructure';
import KeyCode from '../KeyCode';

interface SelectionArgs<T> {
	allItems: T[];
	selectedItems: T[];
	clickedRowIndex: number;
	multiSelect: boolean;
	shiftKey: boolean;
	ctrlKey: boolean;
}

export function getNewSelection<T>({
	allItems,
	selectedItems,
	clickedRowIndex,
	shiftKey,
	ctrlKey,
	multiSelect
}: SelectionArgs<T>): T[] {

	// Caso normal. Paso a seleccionar sólo esto
	if (!multiSelect || (!shiftKey && !ctrlKey))
		return [allItems[clickedRowIndex]];

	// Pulsado ctrl. 
	if (ctrlKey && !shiftKey)
		return withCtrl(allItems, selectedItems, clickedRowIndex);

	// Pulsado shift
	if (!ctrlKey && shiftKey)
		return withShift(allItems, selectedItems, clickedRowIndex);

	// Ctrl + Shift
	return withCtrlAndShift(allItems, selectedItems, clickedRowIndex);

}

function withShift<T>(allItems: T[], selectedItems: T[], clickedRowIndex: number) {

	// Busco desde el primer elemento seleccionado hasta el índice actual y dejo
	// esos como seleccionados, descartando el resto. Mantengo siempre como primer elemento
	// seleccionado el original por si luego se hace shift+click en otra dirección, y el
	// elemento clickado como último elemento, para que se quede con el "foco"


	const item = allItems[clickedRowIndex];

	// Si no había nada, selecciono el item y ya está
	if (selectedItems.length === 0)
		return [item];

	// Si el item seleccionado es el inicial nos quedamos sólo con él
	if (selectedItems[0] === item)
		return [item];

	const firstIndex = allItems.indexOf(selectedItems[0]);

	const newSelection = [selectedItems[0]];

	let maxIterations = allItems.length * 2;

	// No puedo hacer un slice porque quiero mantener al principio el primer
	// item seleccionado para que siga siendo la referencia
	let index = firstIndex;
	do {
		index += firstIndex < clickedRowIndex ? 1 : -1;
		newSelection.push(allItems[index]);

		// Por si acaso vuelve a haber un fallo en el algoritmo de selección
		if (!maxIterations--) {
			throw Error('Detectado bucle infinito en el algoritmo de selección del DataGrid');
		}
	}
	while (index !== clickedRowIndex);

	return newSelection;
}

function withCtrl<T>(allItems: T[], selectedItems: T[], clickedRowIndex: number) {
	// Hago un toggle del item pulsado, colocándolo para que se convierta en
	// referencia si se hace una selección con shift
	const item = allItems[clickedRowIndex];
	return selectedItems.includes(item) ? selectedItems.filter(x => x !== item) : [item, ...selectedItems];
}

function withCtrlAndShift<T>(allItems: T[], selectedItems: T[], clickedRowIndex: number) {

	// Pulsado ctrl + shift. Siempre añade desde el inicio

	const newSelection = [...selectedItems];

	const firstIndex = allItems.indexOf(newSelection[0]);
	const from = firstIndex < clickedRowIndex ? firstIndex : clickedRowIndex;
	const to = clickedRowIndex > firstIndex ? clickedRowIndex : firstIndex;

	for (const item of allItems.slice(from, to + 1)) {
		if (!selectedItems.includes(item))
			newSelection.push(item);
	}

	return newSelection;

}

export function translateKeyCodeToRowIndex<T>(allItems: T[], selectedItems: T[], keyCode: number): Result<number> {

	// Esto se basa en que getNewSelection *SIEMPRE*  mantiene el último item seleccionado al
	// final del array, por lo que eso me permite identificar el item que tiene "el foco" y
	// a partir del cual puedo calcular cuál es row index al que se quiere llegar

	if (selectedItems.length === 0)
		return Result.error();

	const lastSelectedItem = selectedItems[selectedItems.length - 1];
	const lastSelectedIndex = allItems.indexOf(lastSelectedItem);

	// Esto no debería pasar, pero por si acaso
	if (lastSelectedIndex === -1)
		throw Error('El item seleccionado actualmente no está entre los items del grid');

	// El tamaño de página debería ser calculado dinámicamente, pero mejor esto que nada
	const PAGE_SIZE = 10;

	let nextRowIndex = ({
		[KeyCode.KEY_UP]: lastSelectedIndex - 1,
		[KeyCode.KEY_DOWN]: lastSelectedIndex + 1,
		[KeyCode.KEY_HOME]: 0,
		[KeyCode.KEY_END]: allItems.length - 1,
		[KeyCode.KEY_PAGE_UP]: lastSelectedIndex - PAGE_SIZE,
		[KeyCode.KEY_PAGE_DOWN]: lastSelectedIndex + PAGE_SIZE,
	})[keyCode];

	// Teclas no reconocidas
	if (typeof nextRowIndex === 'undefined')
		return Result.error();

	nextRowIndex = Math.max(0, nextRowIndex);
	nextRowIndex = Math.min(nextRowIndex, allItems.length - 1);

	return Result.ok(nextRowIndex);
}
